import {Component, Input, OnChanges, OnInit, SimpleChanges} from '@angular/core';
import {FormControl, ReactiveFormsModule} from '@angular/forms';

import {FieldConfig, FieldOption} from '../../../models/form-config.model';

/**
 * RadioField — gruppo di radio button.
 *
 * Supporta opzioni come string[], number[] o FieldOption[].
 * Ogni opzione genera un <input type="radio"> collegato al FormControl
 * tramite ReactiveFormsModule.
 */
@Component({
  selector: 'app-radio-field',
  standalone: true,
  imports: [ReactiveFormsModule],
  templateUrl: './radio-field.component.html',
  styleUrl: './radio-field.component.scss',
})
export class RadioFieldComponent implements OnInit, OnChanges {
  @Input({required: true}) field!: FieldConfig;
  @Input({required: true}) control!: FormControl;

  /** Opzioni normalizzate per il template. */
  normalizedOptions: FieldOption[] = [];

  /** ID base univoco generato per l'accessibilità. */
  groupId: string = '';

  /**
   * Valore all'apertura del form, cioè quello salvato: il form è costruito con
   * il prefill prima che questo campo nasca, quindi il primo valore del
   * control è quello arrivato dal backend. `null` in inserimento.
   */
  private valoreSalvato: unknown = null;

  ngOnChanges(changes: SimpleChanges): void {
    // Un control nuovo è un form ricostruito (es. prefill arrivato dopo).
    if (changes['control']) {
      this.valoreSalvato = this.control?.value ?? null;
    }
  }

  /**
   * True per l'opzione salvata, nei soli riquadri (`radioCards`): il suo bordo
   * resta marcato anche quando la scelta cambia, così si vede da dove si parte.
   */
  isSalvata(value: unknown): boolean {
    return !!this.field.radioCards && this.valoreSalvato !== null && value === this.valoreSalvato;
  }

  ngOnInit(): void {
    this.groupId = `radio-${this.field.key}-${Math.random().toString(36).slice(2, 7)}`;
    this.normalizedOptions = this.normalizeOptions();
  }

  optionId(index: number): string {
    return `${this.groupId}-${index}`;
  }

  private normalizeOptions(): FieldOption[] {
    if (!this.field.options?.length) return [];
    const first = this.field.options[0];
    if (typeof first === 'string' || typeof first === 'number') {
      return (this.field.options as (string | number)[]).map(v => ({
        label: String(v),
        value: v,
      }));
    }
    return this.field.options as FieldOption[];
  }
}
